type IdleCallback = (deadline: {
  didTimeout: boolean
  timeRemaining: () => number
}) => void

type IdleWindow = Window & {
  requestIdleCallback?: (
    callback: IdleCallback,
    options?: { timeout?: number },
  ) => number
  cancelIdleCallback?: (handle: number) => void
}

export function runWhenIdle(callback: () => void, timeout = 3000) {
  if (import.meta.server) {
    return () => {}
  }

  let cancelled = false
  const run = () => {
    if (!cancelled) {
      callback()
    }
  }

  const idleWindow = window as IdleWindow
  if (idleWindow.requestIdleCallback) {
    const handle = idleWindow.requestIdleCallback(run, { timeout })
    return () => {
      cancelled = true
      idleWindow.cancelIdleCallback?.(handle)
    }
  }

  const handle = window.setTimeout(run, timeout)
  return () => {
    cancelled = true
    window.clearTimeout(handle)
  }
}

export function runAfterLoadWhenIdle(
  callback: () => void,
  timeout = 3000,
  delay = 0,
) {
  if (import.meta.server) {
    return () => {}
  }

  let cancelIdle = () => {}
  let delayHandle: number | undefined
  let cancelled = false

  const run = () => {
    if (!cancelled) {
      cancelIdle = runWhenIdle(callback, timeout)
    }
  }

  const schedule = () => {
    if (delay > 0) {
      delayHandle = window.setTimeout(run, delay)
      return
    }

    run()
  }

  if (document.readyState === 'complete') {
    schedule()
    return () => {
      cancelled = true
      window.clearTimeout(delayHandle)
      cancelIdle()
    }
  }

  window.addEventListener('load', schedule, { once: true })
  return () => {
    cancelled = true
    window.removeEventListener('load', schedule)
    window.clearTimeout(delayHandle)
    cancelIdle()
  }
}
